import { useCallback, useEffect, useRef, useState } from "react";

import { captureLocation, type GeolocationLike } from "./geolocation";

// First-class location readiness for the web duty screen — mirrors the native app, where GPS is
// visible duty status (not an invisible side effect of capturing). The chip this hook drives lets
// a guard grant the browser's location permission UP FRONT (so the first real capture doesn't
// stall on a prompt at the checkpoint) and shows the current fix quality.
//
// Tone doctrine (guard-core patrol-home-presentation): missing/denied GPS is a field reality, not
// a failure — captures still work and the server flags rather than rejects. So "denied" renders
// muted/informational, never as an error.

export type LocationReadiness = "unknown" | "prompt" | "granted" | "denied" | "unavailable";

export interface LocationStatus {
  readiness: LocationReadiness;
  /** Accuracy of the most recent fix in meters, when one has been taken. */
  accuracyMeters: number | null;
  checking: boolean;
  /** Trigger the browser permission prompt (user gesture) and take a fix. Never throws. */
  requestLocation: () => Promise<void>;
}

/** The slice of the Permissions API we use — injectable for tests; null = API absent. */
export interface PermissionsLike {
  query(descriptor: { name: string }): Promise<{
    state: string;
    onchange: (() => void) | null;
  }>;
}

export function useLocationStatus(options?: {
  geolocation?: GeolocationLike | undefined;
  permissions?: PermissionsLike | null;
}): LocationStatus {
  const geolocation = options?.geolocation ?? defaultGeolocation();
  const permissions = options?.permissions === undefined ? defaultPermissions() : options.permissions;

  const [readiness, setReadiness] = useState<LocationReadiness>(
    geolocation === undefined ? "unavailable" : "unknown",
  );
  const [accuracyMeters, setAccuracyMeters] = useState<number | null>(null);
  const [checking, setChecking] = useState(false);

  const geoRef = useRef(geolocation);
  geoRef.current = geolocation;

  const takeFix = useCallback(async (): Promise<void> => {
    if (geoRef.current === undefined) {
      setReadiness("unavailable");
      return;
    }
    setChecking(true);
    try {
      const fix = await captureLocation({ geolocation: geoRef.current });
      setAccuracyMeters(fix.accuracyMeters);
      if (fix.permission === "granted") {
        setReadiness("granted");
      } else if (fix.permission === "denied") {
        setReadiness("denied");
      } else {
        setReadiness("unavailable");
      }
    } finally {
      setChecking(false);
    }
  }, []);

  // Read the permission state up front (and follow changes) where the Permissions API exists.
  // When the permission is ALREADY granted, take a silent fix so the chip can show quality
  // (no prompt fires for granted permissions).
  useEffect(() => {
    if (geolocation === undefined) {
      return;
    }
    if (!permissions) {
      // Older Safari: no Permissions API — stay "unknown" until the guard taps the chip.
      return;
    }
    let active = true;
    let subscription: { onchange: (() => void) | null } | null = null;
    permissions
      .query({ name: "geolocation" })
      .then((status) => {
        if (!active) {
          return;
        }
        const apply = (state: string): void => {
          if (state === "granted") {
            setReadiness("granted");
            void takeFix();
          } else if (state === "denied") {
            setReadiness("denied");
          } else {
            setReadiness("prompt");
          }
        };
        apply(status.state);
        subscription = status;
        status.onchange = () => apply(status.state);
      })
      .catch(() => {
        // Permissions API rejected the descriptor — behave as if absent.
      });
    return () => {
      active = false;
      if (subscription) {
        subscription.onchange = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { readiness, accuracyMeters, checking, requestLocation: takeFix };
}

function defaultGeolocation(): GeolocationLike | undefined {
  if (typeof navigator !== "undefined" && navigator.geolocation) {
    return navigator.geolocation as unknown as GeolocationLike;
  }
  return undefined;
}

function defaultPermissions(): PermissionsLike | null {
  if (typeof navigator !== "undefined" && navigator.permissions) {
    return navigator.permissions as unknown as PermissionsLike;
  }
  return null;
}

/** Chip presentation for a readiness state — tones follow the guard-core GPS doctrine. */
export function locationChip(status: LocationStatus): {
  label: string;
  tone: "ok" | "info" | "muted";
  actionable: boolean;
} {
  if (status.checking) {
    return { label: "Checking GPS…", tone: "info", actionable: false };
  }
  switch (status.readiness) {
    case "granted":
      return {
        label: status.accuracyMeters !== null ? `GPS ±${Math.round(status.accuracyMeters)}m` : "GPS ready",
        tone: "ok",
        actionable: false,
      };
    case "prompt":
      return { label: "Enable location", tone: "info", actionable: true };
    case "unknown":
      return { label: "Check location", tone: "info", actionable: true };
    case "denied":
      // Field reality, not a failure: captures still work; the server flags instead of rejecting.
      return { label: "GPS off — captures still work", tone: "muted", actionable: false };
    case "unavailable":
      return { label: "GPS unavailable", tone: "muted", actionable: false };
  }
}
